import moment from "moment";
import { ComboBox, IComboBoxOption, Label as FabricLabel, PrimaryButton, DefaultButton, Checkbox, Rating, TextField } from "@fluentui/react";
import React, { Component } from "react";
import { connect } from "react-redux";
import { localize } from "src/l10n";
import SpintrDatePicker from "src/spintr/components/SpintrDatePicker";
import { IApplicationState } from "src/spintr/reducer";
import { SpintrTypes } from "src/typings";
import { Label, Loader, UnstyledButton } from "src/ui";
import InfoDialog from "src/ui/components/Dialogs/InfoDialog";
import { Style } from "src/ui/helpers";
import { uniqueId, validateRequiredTextField } from "src/utils";
import { FormControl } from "../../Forms";
import ErrorMessagebar from "../../Messagebars/ErrorMessagebar";
import "./TinyForm.scss";
import api from "src/spintr/SpintrApi";
import TinyFormTable from "./TinyFormTable";
import SuccessMessagebar from "../../Messagebars/SuccessMessagebar";
import SpintrSpinButton from "./SpintrSpinButton";

interface IProps {
    id: number;
    instance?: any;
    apps?: any;
    buttonMessage: string;
    enableAdvancedSurveys?: boolean;
}

interface IState {
    form?: any,
    isLoading: boolean,
    answers: any[],
    dialogText?: any,
    displayInfoDialog: boolean,
    infoBoxText?: any,
    displayInfoBox: boolean,
    enableFieldValidation?: boolean,
    saveError?: any
    formName?: string;
    hideName?: boolean;
    unavailable: boolean;
    userAnswer?: any;
    viewFormAsInactive?: boolean;
    startDate?: boolean;
    draftSaveSuccess?: boolean;
}

class TinyForm extends Component<IProps, IState> {
    constructor(props) {
        super(props);

        this.state = {
            isLoading: true,
            answers: [],
            displayInfoDialog: false,
            displayInfoBox: false,
            unavailable: false,
        };
    }

    isAppEnabled(id) {
        return !!this.props.apps.items.find((a) => a.id === id && a.enabled);
    }

    componentDidMount() {
        if (!this.isAppEnabled(SpintrTypes.SpintrApp.Forms)) {
            return;
        }

        this.fetchForm();
    }

    fetchForm = (reFetch?: boolean) => {
        if (reFetch) {
            this.setState({
                isLoading: true,
            });
        }

        api.get(`/api/v1/survey/${this.props.id}?fetchAnswers=false&isEdit=false`).then((response) => {
            // Sort the potential answers
            const data = response.data

            // data.surveyquestions.forEach((q) => {
            //     q.alternatives = q.alternatives.sort((a, b) => (a.position > b.position ? 1 : -1))
            // })

            if (data.userAnswer) {
                data.userAnswer.formattedDate = moment(data.userAnswer.createdDate).format("LLL");
                const tempAnswers = [];

                for (const surveyQuestion of data.surveyquestions) {
                    const userAnswer = data.userAnswer.answers.find((userAnswer) => userAnswer.questionId == surveyQuestion.id);

                    if (!userAnswer) {
                        // Ignore question if answer was not found,
                        // it was probably added to the survey after the survey was answered
                        continue;
                    }

                    tempAnswers.push({
                        questionId: surveyQuestion.id,
                        alternatives: userAnswer.alternatives.map((alternative) => ({
                            id: alternative.id,
                            alternativeId: alternative.alternativeid,
                            comment: alternative.comment,
                        })),
                        text: surveyQuestion.type == 6 ? new Date(userAnswer.text) : userAnswer.text,
                        number: userAnswer.number,
                        fileUrl: userAnswer.fileUrl,
                        rows: userAnswer.rows
                    });
                }

                this.setState({ answers: tempAnswers });
            }

            this.setState({
                form: data,
                formName: data.name,
                hideName: data.hideName,
                userAnswer: data.userAnswer,
                // buttonMessage: data.buttonMessage,
                isLoading: false,
                viewFormAsInactive: data.viewFormAsInactive,
                startDate: moment(data.startdate).isBefore(),
                unavailable: moment(data.startdate).isAfter() || (!!data.enddate && moment(data.enddate).isBefore())
            });
        }).catch((error) => {
            if (error &&
                error.response &&
                error.response.data &&
                error.response.data.message &&
                error.response.data.message.length > 0) {
                this.setState({
                    isLoading: false,
                    displayInfoBox: true,
                    formName: error.response.data.name,
                    infoBoxText: localize(error.response.data.message[0]),
                });
            }
        });
    }

    allowEditing = () => {
        if (this.state.unavailable) {
            return false;
        }

        const canAnswer = !this.state.form.hasAnswered ||
            this.state.form.allowUserAnswerChange ||
            this.state.form.allowmultipleanswers;

        return canAnswer;
    }

    allowSaveAsDraft = () => {
        if (!this.allowEditing()) {
            return false;
        }

        return !this.state.form.anonymousAnswers &&
            this.props.enableAdvancedSurveys;
    }

    questionTypes = [{
        typeId: 1,
        render: (q) => {
            const answerObj = this.state.answers.find(answer => answer.questionId === q.id);

            return (
                <div>
                    <TextField
                        disabled={!this.allowEditing()}
                        value={answerObj ? answerObj.text : ""}
                        className="textField question"
                        multiline={!q.singlerow}
                        required={q.isRequired}
                        aria-required={q.isRequired}
                        validateOnFocusIn={q.isRequired}
                        validateOnFocusOut={q.isRequired}
                        validateOnLoad={q.isRequired && !!this.state.enableFieldValidation}
                        onGetErrorMessage={validateRequiredTextField}
                        onChange={(
                            ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
                            newValue?: string
                        ) => {
                            if (answerObj) {
                                const newAnswers = [...this.state.answers];

                                for (const a of newAnswers) {
                                    if (a.questionId === q.id) {
                                        a.text = newValue;
                                    }
                                }

                                this.setState({
                                    answers: newAnswers
                                });
                            } else {
                                this.setState({
                                    answers: [
                                        ...this.state.answers,
                                        {
                                            questionId: q.id,
                                            text: newValue
                                        }
                                    ]
                                });
                            }
                        }}
                    />
                </div>
            )
        }
    }, {
        typeId: 2,
        render: (q) => {
            const answerObj = this.state.answers.find(answer => answer.questionId === q.id);
            const selectedAlternativeId = answerObj && answerObj.alternatives && answerObj.alternatives.length === 1 ? answerObj.alternatives[0].alternativeId : null;

            return (
                <div>
                    <ComboBox
                        disabled={!this.allowEditing()}
                        className="question"
                        dropdownWidth={630}
                        required={q.isRequired}
                        aria-required={q.isRequired}
                        calloutProps={{ directionalHintFixed: true }}
                        selectedKey={selectedAlternativeId}
                        onChange={(event, item: IComboBoxOption) => {
                            if (answerObj) {
                                const newAnswers = [...this.state.answers];

                                this.setState({
                                    answers: newAnswers.map(na => {
                                        if (na.questionId === q.id) {
                                            return {
                                                questionId: q.id,
                                                alternatives: [
                                                    {
                                                        ...q.alternatives.find(a => a.id === item.key),
                                                        alternativeId: item.key
                                                    }
                                                ]
                                            };
                                        } else {
                                            return na;
                                        }
                                    })
                                });
                            } else {
                                this.setState({
                                    answers: [
                                        ...this.state.answers,
                                        {
                                            questionId: q.id,
                                            alternatives: [
                                                {
                                                    ...q.alternatives.find(a => a.id === item.key),
                                                    alternativeId: item.key
                                                }
                                            ]
                                        }
                                    ]
                                });
                            }
                        }}
                        options={q.alternatives.map(a => {
                            return {
                                key: a.id,
                                text: a.text
                            }
                        })}
                    />
                    {!!selectedAlternativeId && q.alternatives.find(x => x.id === selectedAlternativeId).enableCommentIfSelected && (
                        <TextField
                            value={answerObj.alternatives[0].comment}
                            className="textField"
                            label={localize("Kommentar_big")}
                            disabled={!this.allowEditing()}
                            resizable={true}
                            multiline
                            maxLength={1024}
                            required={q.isRequired}
                            onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, newValue?: string) => {
                                this.setState({
                                    answers: this.state.answers.map(na => {
                                        if (na.questionId === q.id) {
                                            return {
                                                ...na,
                                                alternatives: na.alternatives.map((naq) => {
                                                    return {
                                                        ...naq,
                                                        comment: newValue
                                                    }
                                                })
                                            };
                                        }

                                        return na;
                                    })
                                });
                            }}
                        />
                    )}
                </div>
            );
        }
    }, {
        typeId: 3,
        render: (q) => {
            const answerObj = this.state.answers.find(answer => answer.questionId === q.id);
            return (
                <div>
                    {
                        q.alternatives.map((a, i) => {
                            const isChecked = answerObj && !!answerObj.alternatives.find((answer) => answer.alternativeId === a.id);

                            return (
                                <div key={i} style={{
                                    marginTop: i === 0 ? 0 : Style.getSpacingStr(2)
                                }}>
                                    <Checkbox
                                        disabled={!this.allowEditing()}
                                        key={i}
                                        label={a.text}
                                        checked={isChecked}
                                        onChange={(ev: React.FormEvent<HTMLElement>, isChecked: boolean) => {
                                            if (answerObj) {
                                                const newAnswers = [...this.state.answers];

                                                this.setState({
                                                    answers: newAnswers.map(na => {
                                                        if (na.questionId === q.id) {
                                                            if (na.alternatives.find(alternative => alternative.id === a.id)) {
                                                                na.alternatives.splice(na.alternatives.indexOf(na.alternatives.find((naa) => naa.id == a.id)), 1);
                                                                return {
                                                                    ...na,
                                                                    alternatives: na.alternatives
                                                                }
                                                            } else {
                                                                return {
                                                                    ...na,
                                                                    alternatives: [
                                                                        ...na.alternatives,
                                                                        {
                                                                            ...a,
                                                                            alternativeId: a.id
                                                                        }
                                                                    ]
                                                                }
                                                            }
                                                        } else {
                                                            return na;
                                                        }
                                                    })
                                                });
                                            } else {
                                                this.setState({
                                                    answers: [
                                                        ...this.state.answers,
                                                        {
                                                            questionId: q.id,
                                                            alternatives: [
                                                                {
                                                                    ...a,
                                                                    alternativeId: a.id
                                                                }
                                                            ]
                                                        }
                                                    ]
                                                })
                                            }
                                        }} />
                                        {isChecked && a.enableCommentIfSelected && (
                                            <TextField
                                                value={answerObj.alternatives.find((answer) => answer.alternativeId === a.id).comment}
                                                className="textField"
                                                label={localize("Kommentar_big")}
                                                disabled={!this.allowEditing()}
                                                resizable={true}
                                                multiline
                                                maxLength={1024}
                                                required={q.isRequired}
                                                onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, newValue?: string) => {
                                                    this.setState({
                                                        answers: this.state.answers.map(na => {
                                                            if (na.questionId === q.id) {
                                                                return {
                                                                    ...na,
                                                                    alternatives: na.alternatives.map((qa) => {
                                                                        if (qa.alternativeId === a.id) {
                                                                            return {
                                                                                ...qa,
                                                                                comment: newValue
                                                                            }
                                                                        }

                                                                        return qa;
                                                                    })
                                                                };
                                                            }

                                                            return na;
                                                        })
                                                    });
                                                }}
                                            />
                                        )}
                                </div>
                            )
                        })
                    }
                </div>
            );
        }
    }, {
        typeId: 4,
        render: (q) => {
            const answerObj = this.state.answers.find(answer => answer.questionId === q.id);

            return (
                <div>
                    <Rating
                        disabled={!this.allowEditing()}
                        min={1}
                        max={10}
                        rating={answerObj ? answerObj.number : null}
                        allowZeroStars={true}
                        icon="CircleFill"
                        unselectedIcon="CircleRing"
                        onChange={(ev: React.FocusEvent<HTMLElement>, rating: number) => {
                            if (answerObj) {
                                this.setState({
                                    answers: this.state.answers.map(a => {
                                        if (a.questionId === q.id) {
                                            return {
                                                questionId: q.id,
                                                number: rating.toString()
                                            }
                                        } else {
                                            return a;
                                        }
                                    })
                                });
                            } else {
                                this.setState({
                                    answers: [
                                        ...this.state.answers,
                                        {
                                            questionId: q.id,
                                            number: rating.toString()
                                        }
                                    ]
                                });
                            }
                        }}
                    />
                </div>
            );
        }
    }, {
        typeId: 5,
        render: (q) => {
            const answerObj = this.state.answers.find(answer => answer.questionId === q.id);

            if (answerObj?.isLoading) {
                return (
                    <div className="file-loader">
                        <Loader />
                    </div>
                );
            }

            return (
                <div>
                    {
                        (!!answerObj && (answerObj.fileTicket || answerObj.fileUrl)) ? <PrimaryButton onClick={() => {
                            this.setState((prevState) => ({
                                answers: prevState.answers.map((answer) => {
                                    if (answer.questionId == q.id) {
                                        return {
                                            questionId: q.id,
                                            fileTicket: "",
                                            fileDeleted: true,
                                            fileUrl: "",
                                            fileName: "",
                                        }
                                    } else {
                                        return answer;
                                    }
                                })
                            }))
                        }}>{localize("TaBort")} {answerObj.fileName}</PrimaryButton> :
                            <input
                                disabled={!this.allowEditing()}
                                className="file-selector"
                                onChange={(event) => {
                                    // @ts-ignore
                                    const file = event.target.files[0];

                                    const body = new FormData();
                                    const fileTicket = new Date().getTime().toString() + uniqueId();

                                    body.append("type", "7");
                                    body.append("file", file);
                                    body.append("thumbnailSize", "natural");
                                    body.append("ticket", fileTicket);
                                    body.append("anonymous", this.state.form.anonymousAnswers ? "true" : "false");

                                    if (answerObj) {
                                        this.setState({
                                            answers: this.state.answers.map((a) => {
                                                if (a.questionId === q.id) {
                                                    return {
                                                        ...a,
                                                        isLoading: true,
                                                    };
                                                }

                                                return a;
                                            }),
                                        });
                                    } else {
                                        this.setState({
                                            answers: [
                                                ...this.state.answers,
                                                {
                                                    questionId: q.id,
                                                    isLoading: true,
                                                },
                                            ],
                                        });
                                    }

                                    api.post("/api/uploads", body).then((response) => {
                                        this.setState({
                                            answers: this.state.answers.map(a => {
                                                if (a.questionId === q.id) {
                                                    return {
                                                        questionId: q.id,
                                                        fileTicket,
                                                        fileName: file.name,
                                                        fileDeleted: false,
                                                        isLoading: false,
                                                    }
                                                } else {
                                                    return a;
                                                }
                                            })
                                        });
                                    });
                                }}
                                type="file"
                            />
                    }
                </div>
            );
        }
    }, {
        typeId: 6,
        render: (q) => {
            const answerObj = this.state.answers.find(answer => answer.questionId === q.id);

            return (
                <div>
                    <SpintrDatePicker
                        disabled={!this.allowEditing()}
                        hideTimeSelect={!q.pickTime}
                        value={answerObj ? answerObj.text : null}
                        onChangeHandler={(date) => {
                            if (answerObj) {
                                this.setState({
                                    answers: this.state.answers.map(a => {
                                        if (a.questionId === q.id) {
                                            return {
                                                questionId: q.id,
                                                text: date
                                            }
                                        } else {
                                            return a;
                                        }
                                    })
                                });
                            } else {
                                this.setState({
                                    answers: [
                                        ...this.state.answers,
                                        {
                                            questionId: q.id,
                                            text: date
                                        }
                                    ]
                                });
                            }
                        }}
                    />
                </div>
            );
        }
    },
    {
        typeId: 7,
        render: (q) => { return null }
    },
    {
        typeId: 8,
        render: (q) => {
            const answerObj = this.state.answers.find(answer => answer.questionId === q.id);

            return (
                <SpintrSpinButton
                    value={answerObj ? answerObj.text : ""}
                    disabled={!this.allowEditing()}
                    onChange={(newValue?: string) => {
                        if (answerObj) {
                            const newAnswers = [...this.state.answers];

                            for (const a of newAnswers) {
                                if (a.questionId === q.id) {
                                    a.text = newValue;
                                }
                            }

                            this.setState({
                                answers: newAnswers
                            });
                        } else {
                            this.setState({
                                answers: [
                                    ...this.state.answers,
                                    {
                                        questionId: q.id,
                                        text: newValue
                                    }
                                ]
                            });
                        }
                    }}
                />
            )
        }
    }, {
        typeId: 9,
        render: (q) => {
            const answerObj = this.state.answers.find(answer => answer.questionId === q.id);

            return (
                <TinyFormTable
                    question={q}
                    answer={answerObj}
                    disabled={!this.allowEditing()}
                    onChange={(updatedAnswerObj) => {
                        if (answerObj) {
                            this.setState({
                                answers: this.state.answers.map((a) => {
                                    if (a.questionId === q.id) {
                                        return updatedAnswerObj;
                                    }

                                    return a;
                                })
                            });
                        } else {
                            this.setState({
                                answers: [
                                    ...this.state.answers,
                                    updatedAnswerObj
                                ]
                            });
                        }
                    }}
                />
            )
        }
    }];

    renderQuestion(q) {
        const questionText = q.question;

        return (
            <FormControl>
                <div className={"TinyForm-part question-type-" + q.type}>
                    <div className="TinyForm-part-question-text">
                        <FabricLabel disabled={!this.allowEditing()} className="surveyHeader" required={q.isRequired}>
                            {
                                questionText
                            }
                        </FabricLabel>
                    </div>
                    {
                        q.description &&
                            q.description.length > 0 &&
                            q.description.toLowerCase() !== "false" &&
                            <div className="TinyForm-part-description-text">
                                <Label size="body-2">
                                    {
                                        q.description.split('\n').map(function (item, key) {
                                            return (
                                                <span key={key}>
                                                    {item}
                                                    <br />
                                                </span>
                                            )
                                        })
                                    }
                                </Label>
                            </div>
                    }
                    <div className="TinyForm-part-input-wrapper">
                        {
                            this.questionTypes.find(qt => qt.typeId === q.type).render(q)
                        }
                    </div>
                </div>
            </FormControl>
        )
    }

    post(isDraft: boolean) {
        const errorStrings = [];

        for (const q of this.state.form.surveyquestions) {
            const answer = this.state.answers.find((a) => a.questionId === q.id);

            if (!isDraft && q.isRequired) {
                if (!answer || (q.type === 1 && answer.text.length === 0)) {
                    errorStrings.push(localize("REQUIRED_FIELD_ERROR").replace("{0}", '"' + q.question + '"') + ".");
                }

                if (!answer || (q.type === 8 && answer.text === undefined)) {
                    errorStrings.push(localize("REQUIRED_FIELD_ERROR").replace("{0}", '"' + q.question + '"') + ".");
                }
            }

            if (q.type === 8 && !!answer && q.minValue !== undefined && answer.text < q.minValue) {
                errorStrings.push(localize("SURVEY_VALUE_MIN_VALUE_VALIDATION").replace("{0}", '"' + q.question + '"').replace("{1}", q.minValue));
            }

            if (q.type === 8 && !!answer && q.maxValue !== undefined && answer.text > q.maxValue) {
                errorStrings.push(localize("SURVEY_VALUE_MAX_VALUE_VALIDATION").replace("{0}", '"' + q.question + '"').replace("{1}", q.maxValue));
            }

            if (q.type === 9 && !!answer) {
                for (const r of answer.rows) {
                    for (const c of r.columns) {
                        const questionColumn = q.columns.find(x => x.id === c.surveyTableColumnId);

                        if (!isDraft && q.isRequired && c.text === undefined) {
                            errorStrings.push(localize("REQUIRED_FIELD_ERROR").replace("{0}", '"' + questionColumn.text + '"') + ".");
                        }

                        if (questionColumn.type !== 8) {
                            continue;
                        }

                        if (questionColumn.minValue !== undefined && c.text < questionColumn.minValue) {
                            errorStrings.push(localize("SURVEY_VALUE_MIN_VALUE_VALIDATION").replace("{0}", '"' + questionColumn.text + '"').replace("{1}", questionColumn.minValue));
                        }

                        if (questionColumn.maxValue !== undefined && c.text > questionColumn.maxValue) {
                            errorStrings.push(localize("SURVEY_VALUE_MAX_VALUE_VALIDATION").replace("{0}", '"' + questionColumn.text + '"').replace("{1}", questionColumn.maxValue));
                        }
                    }
                }
            }
        };

        if (errorStrings.length > 0) {
            this.setState({
                isLoading: true
            }, () => {
                this.setState({
                    isLoading: false,
                    saveError: errorStrings,
                    enableFieldValidation: true
                });
            });

            return;
        }

        this.setState({
            isLoading: true,
            enableFieldValidation: true
        }, () => {
            const answers = this.state.answers.map((answer) => {
                const question = this.state.form.surveyquestions.find((sq) => sq.id === answer.questionId);

                if (question.type === 6) {
                    return {
                        ...answer,
                        text: question.pickTime ?
                            moment(answer.text).format("YYYY-MM-DD HH:mm") :
                            moment(answer.text).format("YYYY-MM-DD")
                    }
                }

                return answer;
            });

            const data = {
                formId: this.state.form.id,
                answers,
                isDraft
            };

            api.post("/api/survey/answer", data).then(() => {
                if (isDraft) {
                    this.fetchForm();
                } else {
                    this.setState({
                        isLoading: false,
                        displayInfoBox: true,
                        draftSaveSuccess: false,
                        infoBoxText: this.state.form.confirmationMessage
                            ? this.state.form.confirmationMessage
                            : localize("TackForDittSvar"),
                    });
                }
            }).catch((error) => {
                if (error &&
                    error.response &&
                    error.response.data) {
                    const arr = error.response.data.message || error.response.data.errorlist || [];

                    if (!!arr && arr.length > 0) {
                        this.setState({
                            isLoading: false,
                            dialogText: localize(arr[0]),
                            displayInfoDialog: true
                        });
                    }
                }
            });
        });
    }

    render() {
        if (!this.isAppEnabled(SpintrTypes.SpintrApp.Forms)) {
            return (
                <div className="TinyForm-wrapper">
                    <div className="TinyForm-info">
                        <Label>
                            {
                                localize("RattigheterEllerInnehallSaknas")
                            }
                        </Label>
                    </div>
                </div>
            )
        }

        if (this.state.displayInfoBox) {
            return (
                <div className="TinyForm-wrapper">
                    {!this.state.hideName && (
                        <div className="TinyForm-header">
                            <Label as="h2" size="h2">
                                {
                                    this.state.formName
                                }
                            </Label>
                        </div>)}
                    <div className="TinyForm-info">
                        <Label>
                            {
                                this.state.infoBoxText
                            }
                        </Label>
                        {!!this.state.form && this.state.form.allowUserAnswerChange && <PrimaryButton onClick={() => {
                            this.setState({
                                displayInfoBox: false
                            });

                            this.fetchForm(true);
                        }} text={localize("CHANGE_ANSWER")} />}
                    </div>
                </div>
            )
        }

        if (this.state.isLoading) {
            return (
                <Loader />
            )
        }

        const formIsUnavailable = !this.state.startDate || this.state.viewFormAsInactive && this.state.unavailable;

        return (
            <div className="TinyForm-wrapper" role="form">
                <form>
                    {!this.state.hideName && (
                        <div className="TinyForm-header">
                            <Label as="h2" size="h2">
                                {this.state.formName}
                            </Label>
                        </div>
                    )}
                    {this.state.draftSaveSuccess && (
                        <div className="draft-save-success">
                            <SuccessMessagebar
                                message={localize("DRAFT_SAVED")}
                                onDismiss={() => {
                                    this.setState({
                                        draftSaveSuccess: false
                                    });
                                }}
                            />
                        </div>
                    )}
                    {this.state.saveError && this.state.saveError.length > 0 && (
                        <ErrorMessagebar
                            errorList={this.state.saveError}
                            onDismiss={() => {
                                this.setState({
                                    saveError: [],
                                });
                            }}
                        />
                    )}
                    {formIsUnavailable &&
                        <div className="TinyForm-info">
                            <Label style={{ fontStyle: "italic" }}>{localize("FormularInaktuell")}</Label>
                        </div>
                    }
                    {!formIsUnavailable && (
                        <div>
                            {this.state.form.userAnswer?.isDraft && (
                                <div className="draft-info">
                                    <Label>{localize("SURVEY_DRAFT_INFO").replace("{0}", this.state.form.userAnswer?.formattedDate)}</Label>
                                    <UnstyledButton
                                        onClick={() => {
                                            this.setState({
                                                isLoading: true
                                            }, () => {
                                                api.delete("/api/v1/survey/answers/" + this.state.form.userAnswer.id).then(() => {
                                                    this.fetchForm();
                                                }).catch(() => {});
                                            });
                                        }}>
                                        <Label color="light-blue">
                                            {localize("REMOVE_DRAFT")}
                                        </Label>
                                    </UnstyledButton>
                                    {/* <PrimaryButton
                                        text={localize("TaBort")}
                                        onClick={() => {}}
                                    /> */}
                                </div>
                            )}
                            {
                                this.state.form.surveyquestions.map((q, i) => {
                                    return (
                                        <div key={i}>
                                            {
                                                this.renderQuestion(q)
                                            }
                                        </div>
                                    )
                                })
                            }
                        </div>
                    )}
                    <div className="TinyForm-footer">
                        {this.allowSaveAsDraft() && <DefaultButton onClick={() => this.post(true)} text={localize("SparaSomUtkast")} />}
                        {this.allowEditing() && <PrimaryButton aria-label={this.state.form.buttonMessage ?? localize("Skicka")} onClick={() => this.post(false)}>
                            {
                                this.state.form.buttonMessage ?? localize("Skicka")
                            }
                        </PrimaryButton>}
                    </div>
                </form>
                {
                    !!this.state.displayInfoDialog && (
                        <InfoDialog
                            message={this.state.dialogText}
                            show={this.state.displayInfoDialog}
                            onDismiss={() => {
                                this.setState({
                                    displayInfoDialog: false
                                });
                            }}
                        />
                    )
                }
            </div>
        );
    }
}

const mapStateToProps = (state: IApplicationState, props) => {
    return {
        ...props,
        instance: state.instance,
        apps: state.app,
        enableAdvancedSurveys: state.instance.get("enableAdvancedSurveys")
    };
};

export default connect(mapStateToProps)(TinyForm);